import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import { getOidcConfig } from "@/config/auth";
import { clearStudyGroupViewRestoration } from "@/features/study-group-view-state/clearStudyGroupViewRestoration";
import { userQueryKeys } from "@/hooks/userQueryKeys";
import {
  forgetAuthenticatedUserId,
  resolveAuthenticatedUserId,
} from "@/lib/authenticatedUserIdentity";
import { navigateToOidcLogout } from "@/lib/authNavigation";
import { authApi } from "@/lib/humhub-api/auth";
import type { User } from "@/lib/humhub-api/types";
import i18n from "@/lib/i18n";
import { removeUserDraftsBeforeLogout } from "@/lib/safeAppNavigation";
import { setLastStudyGroupId } from "@/lib/storage/studyGroupStorage";

const getLogoutLanguage = () => {
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;
  return currentLanguage?.startsWith("ja") ? "ja" : "en";
};

export const useLogout = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [_userInfo, setUserInfo] = useState<User | null>(null);

  /**
   * グローバルログアウト（HumHubとOIDC両方からログアウト）
   * OIDCのトークンはバックエンドで無効化する
   */
  const logout = useCallback(async () => {
    try {
      const currentUser = queryClient.getQueryData<User | null>(
        userQueryKeys.current(),
      );
      const authenticatedUserId = resolveAuthenticatedUserId(currentUser);
      if (authenticatedUserId !== null) {
        await removeUserDraftsBeforeLogout(authenticatedUserId);
      }
      clearStudyGroupViewRestoration(authenticatedUserId ?? undefined);
      forgetAuthenticatedUserId();

      // ユーザー切り替え時に古いスタディグループ選択を持ち越さないようクリア
      setLastStudyGroupId(null);

      // HumHubログアウト
      try {
        await authApi.oidcLogout();
      } catch (backendError) {
        console.warn("⚠️ Backend logout request failed:", backendError);
        // バックエンドのエラーでもフロントエンドのクリーンアップは継続
      }

      // メモリ上のOIDCローカル状態をクリア
      try {
        await auth.removeUser();
      } catch (localOidcError) {
        console.warn("⚠️ Local OIDC user removal failed:", localOidcError);
      }

      // stateのユーザー情報をクリア
      setUserInfo(null);

      // OIDC設定からend_session_endpointを取得
      const oidcConfig = getOidcConfig();
      const endSessionEndpoint = oidcConfig.metadata?.end_session_endpoint;

      if (
        typeof endSessionEndpoint === "string" &&
        endSessionEndpoint.length > 0
      ) {
        // end_session_endpointにクッキーを送信してリダイレクト
        // ID TokenはHttpOnlyクッキーなので、credentials: 'include'でクッキーを送信
        const logoutUrl = new URL(endSessionEndpoint);

        // post_logout_redirect_uriのみクエリパラメータとして設定
        if (typeof oidcConfig.post_logout_redirect_uri === "string") {
          logoutUrl.searchParams.append(
            "post_logout_redirect_uri",
            oidcConfig.post_logout_redirect_uri,
          );
        }

        logoutUrl.searchParams.set("lang", getLogoutLanguage());

        // クッキーを含めてリダイレクト（ブラウザが自動的にクッキーを送信）
        navigateToOidcLogout(logoutUrl.toString());
      } else {
        console.warn(
          "⚠️ Could not find OIDC end session endpoint, falling back to default logout",
        );
        auth.signoutRedirect();
      }
    } catch (error) {
      console.error("❌ Logout error:", error);

      // エラーが発生してもローカル状態はクリア
      forgetAuthenticatedUserId();
      try {
        await auth.removeUser();
      } catch (localOidcError) {
        console.warn("⚠️ Local OIDC user removal failed:", localOidcError);
      }
      setUserInfo(null);
      auth.signoutRedirect();
    }
  }, [auth, queryClient]);

  return { logout };
};
